import React, { useState, useRef, useEffect } from 'react';
import { Leaf, Sprout, Users, Home } from 'lucide-react';
import Logo from '../../components/Logo';
import SocialProofSection from '../../components/landing/SocialProofSection';
import WhatsIncluded from '../../components/landing/WhatsIncluded';
import CTASection from '../../components/landing/CTASection';
import EmailCaptureForm from '../../components/landing/EmailCaptureForm';
import { TESTIMONIALS, PRODUCT_INFO, ProductDetails } from './constants';

export default function RealResultsLanding() {
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const emailSectionRef = useRef<HTMLDivElement>(null);

  const product: ProductDetails = {
    id: PRODUCT_INFO['The Grow Box'].slug,
    name: 'The Grow Box',
    price: PRODUCT_INFO['The Grow Box'].price,
    slug: PRODUCT_INFO['The Grow Box'].slug,
    external_url: PRODUCT_INFO['The Grow Box'].external_url,
    description: 'Everything you need to start growing'
  };

  useEffect(() => {
    if (showEmailCapture && emailSectionRef.current) {
      setTimeout(() => {
        emailSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [showEmailCapture]);

  const handleCtaClick = () => {
    setShowEmailCapture(true);
  };

  const whatsIncludedItems = [
    {
      icon: 'box' as const,
      title: 'Grow Bag',
      description: '10-gallon fabric grow bag'
    },
    {
      icon: 'sprout' as const,
      title: 'Seeds',
      description: 'Zone-matched seed packets'
    },
    {
      icon: 'droplets' as const,
      title: 'Watering System',
      description: 'Self-watering spike included'
    },
    {
      icon: 'package' as const,
      title: 'Fertilizer',
      description: 'Organic plant nutrients'
    },
    {
      icon: 'filetext' as const,
      title: 'Growing Guide',
      description: 'Personalized to your zone'
    },
    {
      icon: 'video' as const,
      title: 'Video Support',
      description: 'Step-by-step tutorials'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Hero = Customer First */}
      <section className="bg-white py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-center mb-8">
            <Logo size="lg" showText={false} variant="full" />
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Featured Customer Story */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl mb-8">
              <img
                src="/Cool Veggie Garden Mock Up.png"
                alt="Real customer grow"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <p className="text-3xl md:text-4xl font-bold mb-4 italic">
                    "I finally didn't kill a plant."
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20"></div>
                    <div>
                      <p className="font-semibold text-lg">Jessica</p>
                      <p className="text-sm text-white/80">Zone 8b • Complete Beginner</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mini Results Section */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              Real Results from Real Growers
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sprout className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">~30 days</h3>
                <p className="text-gray-600">First harvest in about a month</p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Family Fun</h3>
                <p className="text-gray-600">Kids love helping water and harvest</p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Home className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Space Friendly</h3>
                <p className="text-gray-600">Perfect for apartments and balconies</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Product Intro */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Everything You Need in One Box
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              No green thumb required. No guesswork. Just fresh greens growing in your home.
            </p>
            <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-6 inline-block">
              <p className="text-lg font-semibold text-gray-900">
                Complete Grow Box • ${PRODUCT_INFO['The Grow Box'].price}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included - Short List */}
      <WhatsIncluded
        title="What's in Your Grow Box"
        subtitle="Six essential components. One perfect system."
        items={whatsIncludedItems}
      />

      {/* Social Proof Grid - 4-6 testimonials */}
      <SocialProofSection
        testimonials={TESTIMONIALS}
        title="Join Hundreds of Happy Growers"
        columns={2}
      />

      {/* Email Capture Section */}
      {showEmailCapture && (
        <section ref={emailSectionRef} className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto bg-white border-2 border-primary-200 rounded-lg p-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Ready to start your success story?
                </h3>
                <p className="text-gray-600">
                  Enter your email to continue to checkout
                </p>
              </div>
              <EmailCaptureForm
                product={product}
                sourcePage="real-results"
                seedPackage="herb_garden"
                growingEnvironment="indoor"
              />
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      {!showEmailCapture && (
        <CTASection
          headline="Start Your Growing Journey Today"
          subheadline="Join hundreds of successful home growers."
          ctaText="Build My Grow Box"
          ctaAction={handleCtaClick}
        />
      )}
    </div>
  );
}
